import type { RootState } from "core-integration/src/store";
import { getEntry } from "core-integration/src/store/entries/reducers";
import { getCurrentEvent } from "core-integration/src/store/events/reducers";
import { getFixturesForEvent } from "core-integration/src/store/fixtures/reducers";
import { getMyPicksLastUpdated } from "core-integration/src/store/my-team/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { ILoggedInPlayer } from "core-integration/src/store/player/types";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useAdobeContext } from "../../../contexts/AdobeContext";
import { isFirstSave } from "../utils";
import { promotionEligibility, teamSavedModalConfig } from "./config";
import type { TeamSavedModalContext, TeamSavedPromotion } from "./types";
import {
  getLastKickoffTime,
  getStoredNextIndex,
  PickedModal,
  pickModal,
  storeNextIndex,
} from "./utils";

const useTeamSavedModal = () => {
  const [teamSavedPromotion, setTeamSavedPromotion] =
    useState<TeamSavedPromotion | null>(null);
  const { enabled: adobeEnabled } = useAdobeContext();

  const player = useSelector(getPlayerData) as ILoggedInPlayer; // enforced by EntryRoute
  const entry = useSelector((state: RootState) =>
    getEntry(state, player.entry),
  );
  const currentEvent = useSelector(getCurrentEvent);
  const currentEventFixtures = useSelector((state: RootState) =>
    currentEvent ? getFixturesForEvent(state, currentEvent.id) : [],
  );
  const picksLastUpdated = useSelector(getMyPicksLastUpdated);

  const showTeamSavedModal = () => {
    if (!entry) return;

    const { frequency, promotionsByPriority } = teamSavedModalConfig;
    const lastKickoffTime = getLastKickoffTime(currentEventFixtures);
    const context: TeamSavedModalContext = {
      entry,
      adobeEnabled,
      isBeforeLastKickoff:
        lastKickoffTime !== null && Date.now() < lastKickoffTime,
    };
    const isEligible = (promotion: TeamSavedPromotion) =>
      promotionEligibility[promotion](context);

    const show = (modal: PickedModal | null) => {
      if (!modal) return;
      storeNextIndex(modal.index + 1);
      setTeamSavedPromotion(modal.promotion);
    };

    const firstSave = isFirstSave(
      picksLastUpdated,
      currentEvent,
      entry.joined_time,
    );

    switch (frequency) {
      case "off":
        return;
      case "once":
        if (!firstSave) {
          return;
        }
        show(pickModal(promotionsByPriority, isEligible, 0));
        return;
      case "always": {
        const startIndex = firstSave ? 0 : getStoredNextIndex();
        show(pickModal(promotionsByPriority, isEligible, startIndex));
        return;
      }
    }
  };

  const closeTeamSavedModal = () => setTeamSavedPromotion(null);

  return { showTeamSavedModal, closeTeamSavedModal, teamSavedPromotion };
};

export default useTeamSavedModal;
